/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    ICheckoutViewProps,
    IEmailDelivery,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { getCartState } from '@msdyn365-commerce/global-state';
import { ICheckoutConfig, ICheckoutResources as ICheckoutResourcesExt } from '../definition-extensions/checkout.ext.props.autogenerated';
import { find } from 'lodash';

import * as React from 'react';
import { setCheckoutCartInLocalStorage, setOrderPlacedStatusLocalStorage } from '../utilities/datalayer/helpers';
import { trackAddShippingInfo, trackAddPaymentInfo, trackGTMEvent, isUserDataTracked, trackUserData } from '../utilities/datalayer/index';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { GtmEvents } from '../utilities/datalayer/types';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => {
    return (
        <Node {...LineItem}>
            {item}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
            {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
        </Node>
    );
};

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

const _renderCustomTaxLine = (taxAmount: number | null, props: ICheckoutViewPropsExt): JSX.Element | null => {
    const { taxLabel, toBeCalculatedText } = props.resources;

    return (
        <div style={{ marginBottom: '1rem' }}>
            <p className='msc-order-summary__line-tax'>
                <span className='msc-order-summary__label'>{taxLabel}</span>

                <span className='msc-order-summary__value'>{taxAmount && taxAmount > 0 ? `${taxAmount}` : toBeCalculatedText}</span>
            </p>
        </div>
    );
};

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines, ...other }) => {
    // @ts-ignore
    const locale = other.locale;
    // @ts-ignore
    const props = other.props;
    // @ts-ignore
    const taxAmount = other.taxAmount;

    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {locale.toLowerCase() == 'en-gb' ? _renderCustomTaxLine(taxAmount, props) : lines.tax}
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};
interface ICheckoutViewPropsExt extends Omit<ICheckoutViewProps, 'config'> {
    resources: ICheckoutResourcesExt;
    config: ICheckoutConfig;
}
// tslint:disable-next-line:max-func-body-length
const CheckoutView: React.FC<ICheckoutViewPropsExt> = props => {
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        config: { showKeepShoppingButton } = {}
    } = props;

    const locale = props.checkoutProps.moduleProps.context.request.locale;
    const taxAmount = props?.data?.checkout?.result?.checkoutCart?.cart?.TaxAmount;

    const extraOrdersSummaryEu = {
        taxAmount: taxAmount,
        locale: locale,
        props: props
    };
    const [greeting, setGreeting] = React.useState({
        status: false,
        greetingMessageCAP: '',
        greetingSignCAP: ''
    });

    const [listenersAdded, setListenersAdded] = React.useState<Boolean>(false);

    const getCartProps = async () => {
        const activeCartState = await getCartState(props.context?.actionContext);
        const checkoutCartState = props?.data?.checkout?.result?.checkoutCart;
        // prettier-ignore
        // @ts-ignore
        const extPropsToRemove = checkoutCartState?.cart?.ExtensionProperties?.filter((fieldVal: { Key: string }) => fieldVal.Key === 'ExtPropsRemove_CAP')[0]?.Value?.BooleanValue;
        // prettier-ignore
        if (extPropsToRemove === true) {
            const extKeysToRemove = ['ActiveCartId_CAP', 'CheckoutCartId_CAP', 'GreetingMessage_CAP', 'GreetingSign_CAP', 'billing', 'Type_CAP', 'Country_CAP', 'City_CAP', 'ZipCode_CAP', 'OrgName_CAP',
                'VatNum_CAP', 'FiscalCode_CAP', 'PEC_CAP', 'SDI_CAP', 'InvoiceCustAccount_CAP', 'Street_CAP', 'StreetNumber_CAP', 'FirstName_CAP', 'LastName_CAP'];

            const removeExtProps = async () => {
                const extProps = checkoutCartState?.cart?.ExtensionProperties;
                const extPropsToEmpty = [];
                if (extProps) {
                    for (const propToRemove of extKeysToRemove) {
                        if (find(extProps, (obj: { Key: string; }): boolean => obj.Key === propToRemove)) {
                            extPropsToEmpty.push({
                                Key: propToRemove,
                                Value: {}
                            });
                        }
                    }
                    extPropsToEmpty.push({
                        Key: 'ExtPropsRemove_CAP',
                        Value: {
                            BooleanValue: false
                        }
                    });
                }
                await activeCartState?.updateExtensionProperties({ newExtensionProperties: extPropsToEmpty });
                await checkoutCartState?.updateExtensionProperties({ newExtensionProperties: extPropsToEmpty });
            };
            void removeExtProps();
        } else {
            // prettier-ignore
            // @ts-ignore
            const greetingMessageCAP = checkoutCartState?.cart?.ExtensionProperties?.filter((greetingVal: { Key: string }) => greetingVal.Key === 'GreetingMessage_CAP')[0]?.Value?.StringValue;
            // prettier-ignore
            // @ts-ignore
            const greetingSignCAP = checkoutCartState?.cart?.ExtensionProperties?.filter((greetingVal: { Key: string }) => greetingVal.Key === 'GreetingSign_CAP')[0]?.Value?.StringValue;
            setGreeting({
                status:
                    (greetingMessageCAP !== null && greetingMessageCAP !== undefined && greetingMessageCAP !== '') ||
                    (greetingSignCAP !== null && greetingSignCAP !== undefined && greetingSignCAP !== ''),
                greetingMessageCAP: greetingMessageCAP !== undefined ? greetingMessageCAP : '',
                greetingSignCAP: greetingSignCAP !== undefined ? greetingSignCAP : ''
            });
        }
    };

    const addCheckoutStepEventTracking = (section: string, gtmEvent: GtmEvents) => {
        const domSection = document.querySelector(`[data-m-t="${section}"]`);

        if (!domSection) {
            return;
        }

        const onIntersection: IntersectionObserverCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Trigger the event when the section becomes visible
                    trackGTMEvent({
                        gtmEvent,
                        context: props.context,
                        cart: props.data.checkout.result?.checkoutCart.cart,
                        products: props.data.products.result || ([] as SimpleProduct[])
                    });

                    // Optionally disconnect the observer if you only need the event to fire once
                    observer.disconnect();
                }
            });
        };

        const observerOptions: IntersectionObserverInit = {
            root: null,
            rootMargin: '0px',
            threshold: 0.5 // Trigger when 50% of the section is visible
        };

        const observer: IntersectionObserver = new IntersectionObserver(onIntersection, observerOptions);
        observer.observe(domSection);

        return () => {
            observer.disconnect();
        };
    };

    const addPaymentSectionEventTracking = () => {
        const paymentMethodsSection = document.querySelector('[data-m-t="checkout-section-container"]');

        if (!paymentMethodsSection) {
            return;
        }

        const onIntersection: IntersectionObserverCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Trigger the event when the section becomes visible
                    trackAddPaymentInfo(
                        props.context,
                        props.data.checkout.result?.checkoutCart.cart,
                        props.data.products.result || ([] as SimpleProduct[])
                    );

                    // Optionally disconnect the observer if you only need the event to fire once
                    observer.disconnect();
                }
            });
        };

        const observerOptions: IntersectionObserverInit = {
            root: null,
            rootMargin: '0px',
            threshold: 0.5 // Trigger when 50% of the section is visible
        };

        const observer: IntersectionObserver = new IntersectionObserver(onIntersection, observerOptions);
        observer.observe(paymentMethodsSection);

        return () => {
            observer.disconnect();
        };
    };

    React.useEffect(() => {
        let cleanupFunctions: Function[] = []; // Array to hold all cleanup functions

        const cart = props.data.checkout.result?.checkoutCart.cart;
        const customer = props.data.customerInformation.result;

        if (!isUserDataTracked() && cart) {
            trackUserData(props.context, customer, !!cart.LoyaltyCardId);
        }

        if (canShow && !listenersAdded) {
            // Send gtm event
            trackAddShippingInfo(
                props.context,
                props.data.checkout.result?.checkoutCart.cart,
                props.data.products.result || ([] as SimpleProduct[])
            );

            addCheckoutStepEventTracking('custom-checkout-shipping-address', GtmEvents.CHECKOUT_ADD_ADDRESS_INFO);
            addCheckoutStepEventTracking('filtered-delivery-options', GtmEvents.CHECKOUT_ADD_DELIVERY_INFO);
            addCheckoutStepEventTracking('checkout-invoice', GtmEvents.CHECKOUT_ADD_INVOICE_INFO);
            addCheckoutStepEventTracking('checkout-guest-profile', GtmEvents.CHECKOUT_ADD_EMAIL_INFO);
            addCheckoutStepEventTracking('checkout-section-container', GtmEvents.CHECKOUT_ADD_PAYMENT_INFO);

            addPaymentSectionEventTracking();

            void getCartProps();
            const handlePlaceOrderClick = () => {
                setOrderPlacedStatusLocalStorage(true);
                setCheckoutCartInLocalStorage(props.data.checkout.result?.checkoutCart.cart);
            };

            // Find all buttons and add event listeners
            const placeOrderButtons = document.querySelectorAll('.ms-checkout__btn-place-order');
            placeOrderButtons.forEach(button => {
                button.addEventListener('click', handlePlaceOrderClick);

                // Add to cleanupFunctions array
                cleanupFunctions.push(() => button.removeEventListener('click', handlePlaceOrderClick));
            });
            // Check if there is at least one button to observe
            if (placeOrderButtons.length > 0) {
                setListenersAdded(true);
                const observer = new MutationObserver(mutationsList => {
                    for (const mutation of mutationsList) {
                        if (
                            mutation.type === 'attributes' &&
                            mutation.target instanceof Element &&
                            mutation.target.classList.contains('is-busy')
                        ) {
                            setOrderPlacedStatusLocalStorage(true);
                            setCheckoutCartInLocalStorage(props.data.checkout.result?.checkoutCart.cart);
                        }
                    }
                });

                // Observe the first button
                observer.observe(placeOrderButtons[0], { attributes: true });

                // Add observer disconnect to the cleanup functions
                cleanupFunctions.push(() => observer.disconnect());
            }
        }

        // Return a cleanup function that runs all cleanup functions
        return () => cleanupFunctions.forEach(clean => clean());
    }, [canShow]); // Ensure listenersAdded is also a dependency if its value can change

    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <div className={'row'}>
                            <div className={'col-lg-7 '}>
                                <Node {...mainProps}>
                                    {guidedForm}
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    <Node {...mainControlProps}>
                                        {placeOrderButton}
                                        {showKeepShoppingButton && keepShoppingButton}
                                    </Node>
                                </Node>
                            </div>
                            <div className={'col-lg-4 offset-lg-1'}>
                                <Node {...sideProps}>
                                    {orderSummary && <OrderSummaryComponent {...orderSummary} {...extraOrdersSummaryEu} />}
                                    <Node {...sideControlFirstProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        {placeOrderButton}
                                        {showKeepShoppingButton && keepShoppingButton}
                                    </Node>
                                    {greeting.status ? (
                                        <div className={'ms-checkout__greeting'}>
                                            <h3>
                                                <span className={'icon-gift'} />
                                                {props.resources.greetingTitle}
                                            </h3>
                                            <div>
                                                <div>
                                                    <label>{props.resources.greetingMessage} </label>{' '}
                                                    <span>{greeting.greetingMessageCAP}</span>
                                                </div>
                                                <div>
                                                    <label>{props.resources.greetingSign} </label> <span>{greeting.greetingSignCAP}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={'ms-checkout__greeting'}>
                                            {/* tslint:disable-next-line:react-a11y-anchors */}
                                            <h4>
                                                <a href={'/cart'}>
                                                    <span className={'icon-gift'} /> {props.resources.addGreetingText}
                                                </a>
                                            </h4>
                                        </div>
                                    )}
                                    <div className={'ms-checkout__greeting'}>
                                        {/* tslint:disable-next-line:react-a11y-anchors */}
                                        <h4>
                                            <a href={'/cart'}>
                                                <span className={'icon-cart-btn'} /> {props.resources.insertModifyYourPromoCode}
                                            </a>
                                        </h4>
                                    </div>

                                    {lineItems && <LineItemsComponent {...lineItems} />}

                                    <Node {...sideControlSecondProps}>
                                        <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        {placeOrderButton}
                                        {showKeepShoppingButton && keepShoppingButton}
                                    </Node>
                                </Node>
                            </div>
                        </div>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
